'use client';

import { useState, useEffect, useContext } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { v4 as uuid } from 'uuid';
import { ReactComponent as HideIcon } from '@/public/svgs/global/Icons/Hide.svg';
import { ReactComponent as ShowIcon } from '@/public/svgs/global/Icons/Show.svg';

// context
import { LenisContext } from '@/context/lenis-context';

// formik
import * as yup from 'yup';
import { Formik, Form } from 'formik';

import AccountInput from '@/app/account/_components/AccountInput';
import styles from './DeveloperLoginForm.module.scss';
import {
  createBrowserSupabaseClient,
} from '@/utility/supabase/browser';

type AccountLoginResponse = {
  success?: boolean;
  data?: {
    authenticated: boolean;
    workspaceId: string | null;
    redirectPath: string;
  };
  error?: { message?: string };
};

const formSchema = yup.object().shape(
  {
    emailUsername: yup.string()
      .required('An email or username is required to login'),
    password: yup.string().min(6).required('Password is a required field')
  }
);


export default function DeveloperLoginForm()
{
  // use context
  const lenisContext = useContext(LenisContext);

  // use params
  const params = useSearchParams();

  // use router
  const router = useRouter();

  /* ANCHOR STATE */
  const [disabled, setDisabled] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Show any login error passed through the canonical Auth redirect.
  useEffect(() =>
  {
    const errorMessage = params.get('errorMessage');
    const errorCode = params.get('errorCode');

    if (errorMessage && errorCode)
    {
      lenisContext.addToast({
        message: errorMessage as string,
        type: 'error',
        id: uuid(),
        statusCode: +errorCode
      });

    }

    void createBrowserSupabaseClient('owner').auth.getUser().then(({ data }) =>
    {
      if (data.user)
      {
        lenisContext.startLoader();
        router.push(window.location.origin + '/');
      }
    });

  }, []);

  /* ANCHOR Handle Submit */
  async function HandleSubmit(values: { emailUsername: string, password: string; })
  {
    setDisabled(true);

    try
    {
      const identifier = values.emailUsername.trim();
      const response = await fetch('/api/account/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, password: values.password }),
      });
      const result = await response.json().catch(() => null) as AccountLoginResponse | null;

      if (!response.ok)
        throw Object.assign(new Error(result?.error?.message || 'Unable to sign in with those credentials.'), { statusCode: response.status });

      const data = result?.data;
      if (!data || typeof data.redirectPath !== 'string')
        throw Object.assign(new Error('Unable to sign in with those credentials.'), { statusCode: 401 });

      // after successful login redirect
      lenisContext.startLoader();
      router.replace(data.redirectPath);

    } catch (err: any)
    {
      setDisabled(false);
      lenisContext.addToast({ message: err.message, type: 'error', id: uuid(), statusCode: err.statusCode });

    }
  }
  return (
    <Formik
      initialValues={ { emailUsername: '', password: '' } }
      validationSchema={ formSchema }
      onSubmit={ async (values, { setSubmitting }) =>
      {

        await HandleSubmit(values);

        setTimeout(() =>
        {
          setSubmitting(false);
        }, 500);
      } }>
      {
        ({ values, errors, handleChange, handleBlur }) =>
        {
          return (
            <Form className={ styles.accountLoginForm }>
              <div className={ styles.formInputContainer }>
                <AccountInput
                  id='emailUsername'
                  label='Email or username'
                  hasIcon={ false }
                  hasTooltip={ false }
                  name='emailUsername'
                  type='text'
                  placeholder='name@company.com or username'
                  value={ values.emailUsername }
                  onChange={ handleChange }
                  onBlur={ handleBlur }
                  autoComplete='username'
                  error={ errors.emailUsername } />

                <AccountInput
                  id='password'
                  label='Password'
                  hasTooltip={ false }
                  name='password'
                  type={ showPassword ? 'text' : 'password' }
                  placeholder='Enter Your Password'
                  value={ values.password }
                  onChange={ handleChange }
                  onBlur={ handleBlur }
                  autoComplete='current-password'
                  error={ errors.password }
                  hasIcon
                  leadingIcon={
                    <button
                      type='button'
                      className={ styles.passwordToggle }
                      tabIndex={ -1 }
                      onClick={ () => setShowPassword((current) => !current) }
                      aria-label={ showPassword ? 'Hide password' : 'Show password' }>
                      { showPassword ? <HideIcon /> : <ShowIcon /> }
                    </button>
                  } />

                <div className={ styles.calloutCard }>
                  <p>
                    <strong>Active Beta • </strong>
                    Please reach out to us via email if you have not received your login credentials yet.
                  </p>
                </div>

                <button
                  className={ styles.signInButton }
                  disabled={ disabled }
                  type='submit'>
                  { disabled ? 'Signing in...' : 'Sign in' }
                </button>

                <Link className={ styles.forgotPassword } href='/forgot-password'>Forgot password?</Link>
              </div>
            </Form>
          );
        }
      }
    </Formik>
  );
}
